import React, { useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { LinksData } from '../types';
import { useAuthReducer } from '../hooks/useAuthReducer';
import { useDataReducer } from '../hooks/useDataReducer';
import './Admin.css';

interface FormState {
  formData: LinksData;
  showDataActions: boolean;
}

type FormAction =
  | { type: 'SET_FORM_DATA'; payload: LinksData }
  | { type: 'UPDATE_FORM_FIELD'; payload: { name: string; value: string | boolean } }
  | { type: 'TOGGLE_DATA_ACTIONS' };

const formReducer = (state: FormState, action: FormAction): FormState => {
  switch (action.type) {
    case 'SET_FORM_DATA':
      return { ...state, formData: action.payload };
    case 'UPDATE_FORM_FIELD':
      return {
        ...state,
        formData: {
          ...state.formData,
          [action.payload.name]: action.payload.value,
        },
      };
    case 'TOGGLE_DATA_ACTIONS':
      return { ...state, showDataActions: !state.showDataActions };
    default:
      return state;
  }
};

const initialFormState: FormState = {
  formData: {
    title: '',
    subtitle: '',
    whatsappTextBefore: '',
    whatsappTextAfter: '',
    whatsappLink: '',
    whatsappAvailable: false,
    telegramTextBefore: '',
    telegramTextAfter: '',
    telegramLink: '',
    backgroundColor: '#ffffff',
    facebookPixelId: '',
  },
  showDataActions: false,
};

const Admin: React.FC = () => {
  const [formState, formDispatch] = useReducer(formReducer, initialFormState);
  const navigate = useNavigate();
  const { isAuthenticated, logout, isLoading: authLoading } = useAuthReducer();
  const { 
    data, 
    isLoading: dataLoading, 
    error,
    saving,
    updateData,
    loadData
  } = useDataReducer();

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Load initial data
  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    } else {
    }
  }, [isAuthenticated]);

  // Debug data loading state
  useEffect(() => {
  }, [dataLoading, error, data]);

  // Load data into form when available
  useEffect(() => {
    if (data) {
      formDispatch({ type: 'SET_FORM_DATA', payload: data });
    }
  }, [data]);

  useEffect(() => {
  }, [formState.formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const success = await updateData(formState.formData);
      if (success) {
        alert("Landing page atualizada com sucesso!");
      } else {
        alert("Erro ao atualizar dados. Tente novamente.");
      }
    } catch (error) {
      console.error('Error updating data:', error);
      alert("Erro interno. Tente novamente mais tarde.");
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    formDispatch({
      type: 'UPDATE_FORM_FIELD',
      payload: { name, value: type === 'checkbox' ? checked : value }
    });
  }, []);

  if (authLoading || dataLoading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Carregando...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <p>Erro: {error}</p>
        <button onClick={() => window.location.reload()}>Tentar Novamente</button>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="painel">
        <div className="titulo-painel">   
          <h1>Painel Admin</h1>   
          <div className="header-buttons">
            <button onClick={handleLogout} className="logout-btn">
              Sair
            </button>
          </div>
        </div>

        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="title">Título:</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formState.formData.title}
              onChange={handleInputChange}
              required
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label htmlFor="subtitle">Subtítulo:</label>
            <input
              type="text"
              id="subtitle"
              name="subtitle"
              value={formState.formData.subtitle}
              onChange={handleInputChange}
              required
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label htmlFor="backgroundColor">Cor de Fundo:</label>
            <input
              type="color"
              id="backgroundColor"
              name="backgroundColor"
              value={formState.formData.backgroundColor}
              onChange={handleInputChange}
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label htmlFor="whatsappTextBefore">Título WhatsApp:</label>
            <input
              type="text"
              id="whatsappTextBefore"
              name="whatsappTextBefore"
              value={formState.formData.whatsappTextBefore}
              onChange={handleInputChange}
              required
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label htmlFor="whatsappTextAfter">Subtítulo WhatsApp:</label>
            <input
              type="text"
              id="whatsappTextAfter"
              name="whatsappTextAfter"
              value={formState.formData.whatsappTextAfter}
              onChange={handleInputChange}
              required
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label htmlFor="whatsappLink">Link do WhatsApp:</label>
            <input
              type="url"
              id="whatsappLink"
              name="whatsappLink"
              value={formState.formData.whatsappLink}
              onChange={handleInputChange}
              required
              disabled={saving}
            />
          </div>

          <div className="form-group checkbox-group">
            <label htmlFor="whatsappAvailable">
              <input
                type="checkbox"
                id="whatsappAvailable"
                name="whatsappAvailable"
                checked={formState.formData.whatsappAvailable}
                onChange={handleInputChange}
                disabled={saving}
              />
              WhatsApp Disponível
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="telegramTextBefore">Título Telegram:</label>
            <input
              type="text"
              id="telegramTextBefore"
              name="telegramTextBefore"
              value={formState.formData.telegramTextBefore}
              onChange={handleInputChange}
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label htmlFor="telegramTextAfter">Subtítulo Telegram:</label>
            <input
              type="text"
              id="telegramTextAfter"
              name="telegramTextAfter"
              value={formState.formData.telegramTextAfter}
              onChange={handleInputChange}
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label htmlFor="telegramLink">Link do Telegram:</label>
            <input
              type="url"
              id="telegramLink"
              name="telegramLink"
              value={formState.formData.telegramLink}
              onChange={handleInputChange}
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label htmlFor="facebookPixelId">Facebook Pixel ID:</label>
            <input
              type="text"
              id="facebookPixelId"
              name="facebookPixelId"
              value={formState.formData.facebookPixelId || ''}
              onChange={handleInputChange}
              placeholder="Ex: 913933483951451"
              disabled={saving}
            />
            <small>Deixe em branco para desabilitar o Facebook Pixel</small>
          </div>

          <button type="submit" disabled={saving} className="submit-btn">
            {saving ? 'Atualizando...' : 'Atualizar'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Admin;